import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Aos from 'aos';
import 'aos/dist/aos.css';
import axios from 'axios';
import './index.css';

import TopNav from './TopNav';
import LeftSidebar from './LeftSidebar';
import RightSidebar from './RightSidebar';
import Hero from './Hero';
import WinnerSection from './WinnerSection';
import SPSSRetroScrollbar from './SPSSRetroScrollbar';

import GlitchTransition from '../../Components/GlitchTransition';

const API_URL = process.env.REACT_APP_BASE_URL;

const resolveImage = (p) => {
  if (!p) return null;
  if (/^https?:\/\//i.test(p)) return p;
  return `${API_URL}${p}`;
};

const LOREM =
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.';

const CONTACT_STYLES = {
  person: {
    gradient: 'linear-gradient(135deg, rgba(0,180,255,0.15), rgba(100,60,200,0.15))',
    border: '1px solid rgba(0,180,255,0.2)',
    icon: '\u{1F464}',
    textColor: 'rgba(147,224,252,0.7)',
  },
  whatsapp: {
    gradient: 'linear-gradient(135deg, rgba(37,211,102,0.15), rgba(30,160,80,0.15))',
    border: '1px solid rgba(37,211,102,0.2)',
    icon: '\u{1F4AC}',
    textColor: 'rgba(37,211,102,0.7)',
  },
  instagram: {
    gradient: 'linear-gradient(135deg, rgba(225,48,108,0.15), rgba(130,80,200,0.15))',
    border: '1px solid rgba(225,48,108,0.2)',
    icon: '\u{1F4F7}',
    textColor: 'rgba(225,48,108,0.7)',
  },
};

export default function SPSS2026() {
  const { slug } = useParams();
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [showTopNav, setShowTopNav] = useState(true);
  const [showSidebarNav, setShowSidebarNav] = useState(false);
  const [event, setEvent] = useState(null);

  const [heroState, setHeroState] = useState('loading');

  const heroImg = event?.hero_bg_image ? resolveImage(event.hero_bg_image) : null;
  const restImg = event?.rest_bg_image ? resolveImage(event.rest_bg_image) : null;

  useEffect(() => { Aos.init({ duration: 600, once: true, offset: 60 }); }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = slug
          ? `${API_URL}/api/spss-event/${encodeURIComponent(slug)}`
          : `${API_URL}/api/spss-event/active`;
        const res = await axios.get(url);
        if (cancelled) return;
        setEvent(res.data.event);
        setTimeout(() => Aos.refreshHard(), 0);
      } catch {
        setEvent(null);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    setHeroState(heroImg ? 'loading' : 'failed');
    if (!heroImg) return () => { cancelled = true; };
    const img = new window.Image();
    img.onload = () => { if (!cancelled) setHeroState('ready'); };
    img.onerror = () => { if (!cancelled) setHeroState('failed'); };
    img.src = heroImg;
    return () => { cancelled = true; };
  }, [heroImg]);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const heroElRef = useRef(null);

  const handleScroll = useCallback(() => {
    const y = window.scrollY;
    if (!heroElRef.current) heroElRef.current = document.getElementById('hero');
    const heroEl = heroElRef.current;
    if (heroEl) {
      const pastHero = y + 120 > heroEl.offsetTop + heroEl.offsetHeight;
      setShowTopNav(!pastHero);
      setShowSidebarNav(pastHero);
    }
  }, []);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => { handleScroll(); ticking = false; });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [handleScroll]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-scroll-theme', 'spss');
    return () => root.removeAttribute('data-scroll-theme');
  }, []);

  const aboutParas = [event?.about_text_1, event?.about_text_2].filter((t) => t?.trim());
  const aboutContent = aboutParas.length ? aboutParas : [LOREM];

  const mediaItems = (event?.media_items || []).map((m) => ({
    label: m.label,
    aspect: m.aspect_ratio || '4/3',
    image: resolveImage(m.image),
  }));
  const mediaText = event?.media_card_text || null;
  const mediaQuote = event?.media_card_quote || null;
  const showMedia = mediaItems.length > 0 || mediaText || mediaQuote;

  const docItems = (event?.documentations || []).map((d) => ({
    label: d.label,
    highlight: !!d.is_highlight,
    image: resolveImage(d.image),
  }));

  const contactItems = (event?.contacts || []).map((c) => ({
    icon_type: c.icon_type || 'person',
    name: c.name,
    sub: c.contact_value || c.role,
    link: c.link || null,
  }));

  const footerText = event
    ? event.footer_text || `SPSS ${event.year} \u2014 HIMSTAT BINUS UNIVERSITY`
    : '';

  const logos = {
    himstat: resolveImage(event?.logo_himstat),
    ihmsi: resolveImage(event?.logo_ihmsi),
    spss: resolveImage(event?.logo_spss),
  };

  const sidebarLogo = resolveImage(event?.logo_spss_sidebar);
  const gifs = {
    matrix: resolveImage(event?.gif_matrix),
    ogir: resolveImage(event?.gif_ogir),
    sonic: resolveImage(event?.gif_sonic),
  };

  const heroLogo = resolveImage(event?.logo_spss);

  const renderImageOrPlaceholder = (item) =>
    item.image ? (
      <img src={item.image} alt={item.label || ''} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
    ) : (
      <span style={{ fontFamily: "'VT323', monospace", fontSize: '0.75rem', color: 'rgba(0,200,255,0.3)', position: 'relative', zIndex: 2 }}>{item.label}</span>
    );

  const isDesktop = screenWidth >= 1024;

return (
    /* 1. Remove "pr-[18px]" from the root wrapper */
    <div className="relative min-h-screen overflow-x-hidden" style={{ color: '#e2e8f0', fontFamily: "'Montserrat', 'Open Sans', sans-serif" }}>
      <GlitchTransition />
      
      {/* 2. Change width: '100vw' to width: '100%' on both backgrounds */}
      <div
        className="spss-bg absolute top-0 left-0 pointer-events-none"
        style={{
          zIndex: 0, width: '100%', height: '100%', backgroundColor: '#121226',
          ...(restImg ? { backgroundImage: `url(${restImg})`, backgroundRepeat: 'repeat-y', backgroundPosition: 'top center', contain: 'layout paint' } : {}),
        }}
      />

      {/* Hero background */}
      <div
        className="spss-bg-hero absolute top-0 left-0 pointer-events-none"
        style={{
          zIndex: 0, width: '100%', backgroundColor: '#121226',
          ...(heroImg ? { backgroundImage: `url(${heroImg})`, backgroundRepeat: 'no-repeat', backgroundPosition: 'top center', contain: 'layout paint' } : {}),
          opacity: heroState === 'ready' ? 1 : 0,
          transition: 'opacity 700ms ease',
        }}
      />

      <div
        className="pointer-events-none"
        style={{ zIndex: 1, position: 'absolute', top: 0, left: 0, right: 0, height: '100%', background: 'linear-gradient(180deg, rgba(5,8,20,0) 0%, rgba(5,8,20,0) 15%, rgba(4,7,18,0.25) 35%, rgba(4,7,18,0.55) 60%, rgba(2,4,12,0.75) 85%, rgba(2,4,12,0.85) 100%)', pointerEvents: 'none' }}
      />

      <TopNav visible={isDesktop ? showTopNav : true} logos={logos} />
      <LeftSidebar visible={isDesktop} showNav={showSidebarNav && isDesktop} logo={sidebarLogo} />
      <RightSidebar visible={isDesktop} showNav={showSidebarNav && isDesktop} showTopNav={showTopNav && isDesktop} gifs={gifs} />
      <SPSSRetroScrollbar />

      {/* 3. Balanced side margins for desktop and xl */}
      <main className="relative z-10 ml-0 desktop:ml-[150px] xl:ml-[180px] mr-0 desktop:mr-[120px] xl:mr-[140px]" style={{ contain: 'layout style' }}>
        <div className="w-full flex justify-center">
          <Hero heroState={heroState} logo={heroLogo} />
        </div>

        {/* ── About ─────────────────────────────────────────────────────── */}
        <section id="about" className="spss-about py-16 tablet:py-24 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="spss-card-glow p-8 tablet:p-12" data-aos="fade-up">
              <h2 className="spss-section-title mb-6" data-aos="fade-up">{event?.about_title || 'ABOUT'}</h2>
              <div className="space-y-5" data-aos="fade-up" data-aos-delay="100">
                {aboutContent.map((text, i) => (
                  <p key={i} className="leading-relaxed" style={{ color: 'rgba(210,225,245,0.9)', fontSize: '0.9rem', lineHeight: '1.8', textShadow: '0 0 8px rgba(200,220,255,0.08)' }}>{text}</p>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Media Gallery ─────────────────────────────────────────────── */}
        {showMedia && (
          <section className="py-12 tablet:py-16 px-4">
            <div className="max-w-5xl mx-auto">
              {mediaItems.length > 0 && (
                <div className="grid grid-cols-2 tablet:grid-cols-3 gap-4" data-aos="fade-up">
                  {mediaItems.map((item, i) => (
                    <div
                      key={i}
                      className={`spss-image-placeholder ${i === mediaItems.length - 1 ? 'col-span-2 tablet:col-span-1 justify-self-center w-[calc(50%-0.5rem)] tablet:w-auto' : ''}`}
                      style={{ aspectRatio: item.aspect, borderRadius: '12px' }}
                      data-aos="fade-up" data-aos-delay={i * 100}
                    >
                      {renderImageOrPlaceholder(item)}
                    </div>
                  ))}
                </div>
              )}

              {(mediaText || mediaQuote) && (
                <div className="spss-card mt-6 p-6 tablet:p-8" data-aos="fade-up" data-aos-delay="200">
                  {mediaText && (
                    <p style={{ color: 'rgba(210,225,245,0.85)', fontSize: '0.85rem', lineHeight: '1.7', textShadow: '0 0 6px rgba(200,220,255,0.06)' }}>{mediaText}</p>
                  )}
                  {mediaQuote && (
                    <p className="mt-4 italic" style={{ fontFamily: "'VT323', monospace", fontSize: '0.8rem', color: 'rgba(0,200,255,0.5)' }}>{mediaQuote}</p>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {/* ── Winners: Glance ───────────────────────────────────────────── */}
        <WinnerSection type="glance" title={event?.glance_title} subtitle={event?.glance_subtitle} congrats={event?.glance_congrats} winners={event?.winners_glance} />

        {/* ── Winners: Smile ────────────────────────────────────────────── */}
        <WinnerSection type="smile" title={event?.smile_title} subtitle={event?.smile_subtitle} congrats={event?.smile_congrats} winners={event?.winners_smile} />

        {/* ── Documentation ─────────────────────────────────────────────── */}
        {docItems.length > 0 && (
          <section id="documentation" className="py-12 tablet:py-20 px-4">
            <div className="max-w-5xl mx-auto">
              <div className="spss-card-glow p-4 tablet:p-5 mb-6 text-center" data-aos="fade-up">
                <h2 className="spss-section-title">{event?.documentation_title || 'DOCUMENTATION'}</h2>
              </div>
              <div className="grid grid-cols-2 tablet:grid-cols-3 gap-4" data-aos="fade-up" data-aos-delay="100">
                {docItems.map((img, i) => (
                  <div key={i} className={`spss-image-placeholder ${img.highlight ? 'spss-doc-highlight' : ''}`} style={{ aspectRatio: '4/3', borderRadius: '10px' }} data-aos="fade-up" data-aos-delay={50 + i * 80}>
                    {renderImageOrPlaceholder(img)}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── Contacts ──────────────────────────────────────────────────── */}
        {contactItems.length > 0 && (
          <section id="contacts" className="py-12 tablet:py-20 px-4 pb-24">
            <div className="max-w-4xl mx-auto">
              <div className="spss-card-glow p-4 tablet:p-5 mb-6 text-center" data-aos="fade-up">
                <h2 className="spss-section-title">{event?.contacts_title || 'CONTACTS'}</h2>
              </div>
              <div className="grid grid-cols-1 tablet:grid-cols-3 gap-4" data-aos="fade-up" data-aos-delay="100">
                {contactItems.map((contact, i) => {
                  const style = CONTACT_STYLES[contact.icon_type] || CONTACT_STYLES.person;
                  const inner = (
                    <>
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: style.gradient, border: style.border }}>
                        <span style={{ fontSize: '1.1rem' }}>{style.icon}</span>
                      </div>
                      <div>
                        <p className="font-bold" style={{ fontSize: '0.85rem', color: '#e2e8f0', marginBottom: '2px' }}>{contact.name}</p>
                        <p style={{ fontFamily: "'VT323', monospace", fontSize: '0.75rem', color: style.textColor }}>{contact.sub}</p>
                      </div>
                    </>
                  );
                  return contact.link ? (
                    <a key={i} href={contact.link} target={contact.link.startsWith('http') ? '_blank' : undefined} rel={contact.link.startsWith('http') ? 'noreferrer' : undefined} className="spss-contact-card" aria-label={contact.name}>{inner}</a>
                  ) : (
                    <div key={i} className="spss-contact-card">{inner}</div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Footer — always shown */}
        {footerText && (
          <div className="py-12 text-center">
            <div className="spss-divider mb-6" />
            <p style={{ fontFamily: "'Press Start 2P', monospace", fontSize: '0.5rem', fontWeight: 700, color: 'rgba(147,224,252,0.5)', letterSpacing: '0.12em', textShadow: '0 0 10px rgba(0,200,255,0.15)' }}>{footerText}</p>
          </div>
        )}
      </main>
    </div>
  );
}
